/**
 * SaveToHubDialog — Small modal that saves the just-geocoded project address
 * into the user's HUB (user_addresses collection).
 *
 * Used from GeolocationBanner right after a successful geocode. Persists via
 * POST /api/hub/addresses and, if category=pro and a project_id is passed,
 * also links this project to the address (POST /link-project).
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRO_SUBCATS = ['chantier', 'annonce', 'client', 'bureau'];
const PERSO_SUBCATS = ['maison', 'famille', 'ami', 'vacances'];

export default function SaveToHubDialog({ open, onOpenChange, address, coords, projectId, defaultLabel = '' }) {
  const [label, setLabel] = useState(defaultLabel);
  const [category, setCategory] = useState('pro');
  const [subcategory, setSubcategory] = useState('chantier');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const suggestions = category === 'pro' ? PRO_SUBCATS : PERSO_SUBCATS;

  const submit = async () => {
    const l = (label || '').trim();
    if (!l) { toast.error('Renseigne un nom (ex: Chantier Paris centre)'); return; }
    if (!coords || coords.length !== 2) { toast.error('Coordonnées manquantes'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/hub/addresses`, {
        label: l,
        address: address || '',
        coords,
        category,
        subcategory: (subcategory || '').trim() || null,
        notes: (notes || '').trim() || null,
      }, { withCredentials: true });
      const addr = r.data;
      // For pro category with a project context, immediately link
      if (category === 'pro' && projectId) {
        try {
          await axios.post(`${API}/hub/addresses/${addr.address_id}/link-project`,
            { project_id: projectId }, { withCredentials: true });
        } catch { /* non-blocking */ }
      }
      toast.success('Adresse ajoutée à ton HUB');
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur d\'enregistrement');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="save-to-hub-dialog">
        <DialogHeader>
          <DialogTitle>Enregistrer dans mon HUB</DialogTitle>
          <DialogDescription>
            Retrouve cette adresse dans ta carte perso/pro pour créer des projets ou la partager.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-400">Nom (label)</label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex: Chantier Paris centre"
              data-testid="save-to-hub-label"
              autoFocus
            />
            <div className="text-[11px] text-gray-500 mt-1 truncate">{address}</div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-400">Catégorie</label>
            <div className="flex gap-2 mt-1">
              {['pro', 'perso'].map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => { setCategory(c); setSubcategory((c === 'pro' ? PRO_SUBCATS : PERSO_SUBCATS)[0]); }}
                  className={`px-3 py-1.5 rounded-md text-sm border transition ${category === c
                    ? (c === 'pro' ? 'bg-amber-500/20 border-amber-400 text-amber-100' : 'bg-blue-500/20 border-blue-400 text-blue-100')
                    : 'border-gray-700 text-gray-400 hover:border-gray-500'}`}
                  data-testid={`save-to-hub-cat-${c}`}
                >
                  {c === 'pro' ? '💼 Pro' : '🏠 Perso'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-400">Sous-catégorie</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {suggestions.map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => setSubcategory(s)}
                  className={`px-2 py-1 rounded text-[11px] border ${subcategory === s ? 'bg-emerald-500/20 border-emerald-400 text-emerald-100' : 'border-gray-700 text-gray-400 hover:border-gray-500'}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <Input
              value={subcategory}
              onChange={(e) => setSubcategory(e.target.value)}
              placeholder="Ou tape la tienne…"
              className="mt-2 h-8 text-sm"
              data-testid="save-to-hub-subcat"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-400">Notes (optionnel)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Contact, accès, remarques…"
              rows={2}
              className="text-sm"
              data-testid="save-to-hub-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange?.(false)} disabled={busy} data-testid="save-to-hub-cancel">
            <X className="w-3.5 h-3.5 mr-1" /> Annuler
          </Button>
          <Button onClick={submit} disabled={busy || !label.trim()} data-testid="save-to-hub-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
